<template>
  <div
    :style="{ 'top': this.isTranslateBarOpen ? '40px' : '0px' }"
    class="modal is-active"
    ref="modal"
    role="dialog"
    tabindex="-1"
    aria-modal="true"
    :aria-label="school.name"
  >
    <header
      class="modal-navbar notranslate"
      aria-labelledby="cardLabel"
      :style="{ 'background-color': this.color }"
    >
      <div class="container d-flex justify-content-between align-items-center">
        <div class="nav-left d-flex align-items-center">
          <i
            class="fas fa-arrow-circle-left fa-2x modal-btn-close"
            aria-label="close"
            @click="close"
          ></i>
          <!-- <a class="modal-btn-close" aria-label="close" @click="close">Back&nbsp;&nbsp;</a> -->
          <div>
            <h1 class="is-size-3-desktop is-size-4-touch" id="cardLabel">
              {{ school.name }}
            </h1>
            <p class="is-size-4-desktop is-size-5-touch translate">
              {{ school.tagline }}
            </p>
          </div>
        </div>
        <div class="nav-right">
          <img
            v-if="school.activeLogoImage"
            :src="school.imagesPath + '/logos/' + school.activeLogoImage.name"
            :alt="school.activeLogoImage.alt_text"
            :height="80"
            :width="80"
          />
          <img v-else height="80px" width="80px" :src="'images/default/logo-placeholder.png'" :alt="'Placeholder logo image'">
        </div>
      </div>
    </header>

    <div class="container">
      <div class="columns is-multiline">
        <div class="column is-8-fullhd is-6-desktop is-12-tablet">
          <div
            class="photos-container"
            :style="{ 'background-color': this.lightColor }"
          >
            <div class="card-content img-container">
              <div>
                <img
                  v-if="school.activeBannerImage"
                  :src="
                    school.imagesPath +
                    '/banners/' +
                    school.activeBannerImage.name
                  "
                  :alt="school.activeBannerImage.alt_text"
                />
                <img
                  v-else
                  :src="'images/default/placeholder.png'"
                  :alt="'Placeholder banner image'"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="column is-4-fullhd is-6-desktop is-12-tablet">
          <div
            class="card"
            :style="{ 'background-color': this.lightestColor }"
            role="group"
            aria-labelledby="pop"
          >
            <div
              class="card-header"
              :style="{ 'background-color': this.color }"
            >
              <h2 class="is-size-3-desktop is-size-4-touch" id="pop">
                Points of Pride
              </h2>
            </div>
            <div class="card-content">
              <ol>
                <li>{{ school.pointsOfPride.one }}</li>
                <li>{{ school.pointsOfPride.two }}</li>
                <li>{{ school.pointsOfPride.three }}</li>
                <li>{{ school.pointsOfPride.four }}</li>
                <li>{{ school.pointsOfPride.five }}</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div class="columns is-multiline">
        <div class="column is-8-fullhd is-6-desktop is-12-tablet">
          <div class="card" :style="{ 'background-color': this.lightestColor }">
            <div
              class="card-header"
              :style="{ 'background-color': this.color }"
            >
              <h2 class="is-size-3-desktop is-size-4-touch">Philosophy</h2>
            </div>
            <div class="card-content">
              <p class="philosophy-description" v-html="school.description"></p>
            </div>
          </div>
        </div>
        <div class="column is-4-fullhd is-6-desktop is-12-tablet">
          <div class="map-container notranslate" role="group" aria-label="google map">
            <gmap-map
              :center="center"
              :zoom="12"
              style="width: 100%; height: 100%"
            >
              <gmap-marker
                :position="center"
                :clickable="false"
                :draggable="false"
              ></gmap-marker>
              <gmap-info-window :position="center" :options="infoOptions">
                <p class="has-text-weight-normal">
                  {{ school.name }}
                </p>
                <p>
                  {{ school.address }}<br />
                  {{ school.city }}, {{ school.zipCode }}
                </p>
                <p>
                  {{ school.officePhone }}
                </p>
              </gmap-info-window>
              <gmap-polygon
                :paths="school.polygons"
                :editable="false"
                :options="polygonOptions"
              ></gmap-polygon>
            </gmap-map>
          </div>
        </div>
      </div>

      <div class="columns" v-if="school.categories.length">
        <div class="column is-12">
          <div class="card" :style="{ 'background-color': this.lightestColor }">
            <div
              class="card-header flex-column"
              :style="{ 'background-color': this.color }"
            >
              <h2 class="is-size-3-desktop is-size-4-touch">School Programs</h2>
              <p aria-hidden="true">Click on a program below to learn more</p>
            </div>
            <div class="card-content">
              <div class="columns is-multiline">
                <div
                  class="column is-12-tablet is-6-desktop"
                  v-for="category in school.categories"
                  :key="category.id"
                  role="group"
                  :aria-labelledby="`category-${category.id}`"
                >
                  <h4
                    class="is-size-3-desktop is-size-4-touch"
                    :id="`category-${category.id}`"
                    style="font-weight: 500"
                  >
                    {{ category.name }}
                  </h4>
                  <ul>
                    <li v-for="feature in category.features" :key="feature.id">
                      <div
                        v-if="hasFeature(feature, school.features)"
                        class="
                          d-flex
                          align-items-center
                          feature-name-list-items
                        "
                      >
                        <i
                          class="fas fa-check-circle fa-sm"
                          aria-hidden
                          :style="{ color: school.color, paddingRight: '10px' }"
                        ></i>
                        <p
                          class="
                            is-size-4-desktop is-size-5-touch
                            hover-cursor-pointer
                          "
                          style="text-decoration: underline"
                          tabindex="0"
                          role="button"
                          @keyup.enter="toggleFeature(feature)"
                          @click="toggleFeature(feature)"
                          :aria-expanded="feature.display"
                        >
                          {{ feature.name }}
                        </p>
                      </div>
                      <transition name="slideDown">
                        <p
                          class="is-size-5-desktop is-size-6-touch p-3"
                          :id="'feature-description_' + feature.id"
                          tabindex="-1"
                          v-show="feature.display"
                        >
                          {{ feature.description }}
                        </p>
                      </transition>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <!-- end card-content -->
          </div>
          <!-- end card -->
        </div>
        <!-- end column -->
      </div>
      <!-- end columns -->

      <div class="columns is-multiline" v-if="hasData">
        <div
          class="column is-4-widescreen is-6-desktop is-12-tablet"
          v-if="!parentQsData.isNull"
        >
          <cmps-parent-qs
            :data="parentQsData"
            :headerColor="this.color"
            :bodyColor="this.lightestColor"
          ></cmps-parent-qs>
        </div>
        <!-- <div class="column is-4-widescreen is-6-desktop is-12-tablet" v-if="!letterGradeData.isNull">
          <cmps-letter-grade :data="letterGradeData" :headerColor="this.color" :bodyColor="this.lightestColor"></cmps-letter-grade>
        </div> -->
        <div
          class="column is-4-widescreen is-6-desktop is-12-tablet"
          v-if="!azMeritData.isNull"
        >
          <cmps-az-merit
            :data="azMeritData"
            :headerColor="this.color"
            :bodyColor="this.lightestColor"
          ></cmps-az-merit>
        </div>
          <div
            class="column is-4-widescreen is-6-desktop is-12-tablet"
            v-if="this.school.data.satScores.length > 0"
          >
            <cmps-sat-scores
              :data="satData"
              :headerColor="this.color"
              :bodyColor="this.lightestColor"
            ></cmps-sat-scores>
          </div>
          <div
            class="column is-4-widescreen is-6-desktop is-12-tablet"
            v-if="this.school.data.actScores.length > 0"
          >
            <cmps-act-scores
              :data="actData"
              :headerColor="this.color"
              :bodyColor="this.lightestColor"
            ></cmps-act-scores>
          </div>
        </div>
      </div>
      <!-- end container -->
    </div>
    <!-- end modal -->
</template>

<script>
import Preschool from "./shared/icons/Preschool.vue";
import Elementary from "./shared/icons/Elementary.vue";
import JuniorHigh from "./shared/icons/JuniorHigh.vue";
import HighSchool from "./shared/icons/HighSchool.vue";
import ParentQualitySurvey from "./graphs/ParentQualitySurvey.vue";
import AzMerit from "./graphs/AzMerit.vue";
import LetterGrade from "./graphs/LetterGrade.vue";
import SatScores from "./graphs/SatScores.vue";
import ActScores from "./graphs/ActScores.vue";
import { shadeColor } from "../helpers/functions.js";

export default {
  props: ["school"],
  components: {
    cmpsPreschoolIcon: Preschool,
    cmpsElementaryIcon: Elementary,
    cmpsJuniorHighIcon: JuniorHigh,
    cmpsHighSchoolIcon: HighSchool,
    cmpsParentQs: ParentQualitySurvey,
    cmpsAzMerit: AzMerit,
    cmpsLetterGrade: LetterGrade,
    cmpsSatScores: SatScores,
    cmpsActScores: ActScores,
  },
  data() {
    return {
      validURL: true,
      lightestColor: shadeColor(this.school.color, 0.8),
      lightColor: shadeColor(this.school.color, 0.5),
      color: shadeColor(this.school.color, 0),
      center: { lat: this.school.latitude, lng: this.school.longitude },
      markers: [
        {
          position: { lat: this.school.latitude, lng: this.school.longitude },
        },
      ],
      infoOptions: {
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
      polygonOptions: {
        strokeColor: "blue",
        strokeOpacity: 0.8,
        strokeWeight: 1,
        fillColor: "blue",
        fillOpacity: 0.35,
      },
      // filter: new DataFilter(this.school.data),
      hasData:
        this.school.hasOwnProperty("data") &&
        Object.keys(this.school.data).length > 0,
    };
  }, //end data
  computed: {

    isTranslateBarOpen() {
      return this.$store.state.main.translateBar.active;
    },

    modalOpen() {
      return this.$store.getters.modalOpen;
    },
    parentQsData() {
      return this.getParentQsData();
    },
    letterGradeData() {
      let year = "2016-2017";
      return this.filter.getLetterGradeData(year);
    },
    azMeritData() {
      return this.getAzMeritData();
    },
    satData() {
      return this.getSatData();
    },
    actData() {
      return this.getActData();
    },

  },
  methods: {

    hasFeature(feature, schoolFeatures) {
			let isIncluded = false
			schoolFeatures.map((f) => {
				if (feature.id === f.id) {
          isIncluded = true
        }
			})
			return isIncluded
		},
    toggleFeature(feature) {
        feature.display = !feature.display;
        if (feature.display) {
          setTimeout(() =>
            document.querySelector("#feature-description_" + feature.id).focus()
          );
        }
    },
    close() {
      this.$store.dispatch("toggleMoreModal", {
        school: {},
        active: false,
        returnElement: {},
      });
    },
    closeOnEscapeKeyup(event) {
      if (event.keyCode === 27) {
        this.close();
      }
    },
    onBackButtonPressed() {
      this.close();
    },
    getParentQsData() {
      let isNull = false;
      if (this.school.data.qualityServiceData[0]) {
        if (
          this.school.data.qualityServiceData[0].a_plus === null ||
          this.school.data.qualityServiceData[0].a === null ||
          this.school.data.qualityServiceData[0].b === null
        ) {
          isNull = true;
        }
      } else {
        isNull = true;
      }
      let parentQsData = {
        isNull: isNull,
      };
      if (!isNull) {
        parentQsData.year = this.school.data.qualityServiceData[0].school_year;
        parentQsData.aplus = parseInt(this.school.data.qualityServiceData[0].a_plus);
        parentQsData.a = parseInt(this.school.data.qualityServiceData[0].a);
        parentQsData.b = parseInt(this.school.data.qualityServiceData[0].b);
        parentQsData.lessb =
          100 -
          parseInt(this.school.data.qualityServiceData[0].a_plus) -
          parseInt(this.school.data.qualityServiceData[0].a) -
          parseInt(this.school.data.qualityServiceData[0].b);
      }
      return parentQsData;
    },
    getAzMeritData() {
      let isNull = false;
      if (this.school.data.azMeritPassing[0]) {
        if (
          this.school.data.azMeritPassing[0].passing_MAT === null ||
          this.school.data.azMeritPassing[0].passing_MELA === null
        ) {
          isNull = true;
        }
      } else {
        isNull = true;
      }

      let azMeritData = {
        isNull: isNull,
      };
      if (!isNull) {
        azMeritData.year = this.school.data.azMeritPassing[0].school_year;
        azMeritData.math = this.school.data.azMeritPassing[0].passing_MAT;
        azMeritData.ela = this.school.data.azMeritPassing[0].passing_ELA;
      }
      return azMeritData;
    },
    getSatData() {
      let isNull = false
      let years = []
      let mathScores = []
      let rwScores = []
      if (this.school.data.satScores) {
        this.school.data.satScores.forEach(satScore => {
            if (satScore.math_avg === null || satScore.rw_avg === null) {
              isNull = true
            } else {
            isNull = false
          }
        })
      }

      let satData = {
        isNull: isNull
      }
      if (!isNull) {
        this.school.data.satScores.map(score => {
          years.push(score.school_year)
          mathScores.push(score.math_avg)
          rwScores.push(score.rw_avg)
        })
        satData.years = years
        satData.math = mathScores
        satData.ela = rwScores
      }
      return satData
    },
    getActData() {
      let isNull = false
      let years = []
      let compositeScores = []

      if (this.school.data.actScores) {
        this.school.data.actScores.forEach(score => {
            if (score.avg_score === null) {
              isNull = true
            } else {
            isNull = false
          }
        })
      }

      let actData = {
        isNull: isNull
      }
      if (!isNull) {
        this.school.data.actScores.map(score => {years.push(score.school_year)})
        this.school.data.actScores.map(score => {compositeScores.push(score.avg_score)})
        actData.years = years
        actData.scores = compositeScores
      }
      return actData
    },
  },
  mounted() {
    document.documentElement.classList.add("disable-scrolling");
    this.$refs.modal.focus();
  },
  created() {
    window.addEventListener("keyup", this.closeOnEscapeKeyup);
    window.addEventListener("popstate", this.onBackButtonPressed);
    this.validURL = Object.keys(this.school).length === 0 ? false : true;
  },
  destroyed() {
    document.documentElement.classList.remove("disable-scrolling");
      this.school.features.forEach((f) => {
        f.display = false;
    });
    window.removeEventListener("keyup", this.closeOnEscapeKeyup);
    window.removeEventListener("popstate", this.onBackButtonPressed);
  },
};
</script>

<style lang="scss" scoped>
.modal.is-active {
  display: block;
  overflow-y: auto;
  overflow-x: hidden; //so accessibility tables don't add whitespace when using .aria-table class
  background-color: white;
}
.modal-btn-close {
  margin-right: 20px;
  margin-left: 20px;
  cursor: pointer;
  color: white;
}
.modal-navbar .container {
  padding: 0.75rem 0;
  .nav-left {
    h1,
    p {
      color: white;
      font-family: "Montserrat", sans-serif;
      font-weight: 600;
    }
  }
  .nav-right {
    padding: 0 7px;
  }
}
@media screen and (min-width: 769px) {
  .modal-close {
    margin-right: 25px;
  }
}
section {
  margin: 2.5rem 0;
}
.photos-container {
  display: flex;
  align-items: center;
  box-shadow: 0px 6px 10px #999;
  height: 100%;
  .img-container {
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 50%;
    div {
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      padding: 0.75rem;
    }
    img {
      height: 100%;
      width: 100%;
    }
  }
}
.map-container {
  height: 400px;
  box-shadow: 0px 6px 10px #999;
}
.card {
  height: 100%;
  box-shadow: 0px 6px 10px #999;
  .card-header {
    color: #fff;
    border-bottom: 1px solid black;
    padding: 0.75rem;
    h2 {
      font-weight: 600;
      font-family: "Montserrat", sans-serif;
    }
  }
  .card-content {
    font-family: "Roboto", sans-serif;
    font-size: 1.25rem;
    color: #000;
    ol {
      padding: 0 1rem;
    }
  }
}
.feature-name-list-item:hover,
.feature-name-list-item:hover > i {
  cursor: pointer;
  color: hsl(217, 71%, 53%) !important;
}
@media screen and (min-width: 1024px) {
  .map-container {
    height: 100%;
  }
}
@media screen and (min-width: 1408px) {
  .philosophy-description {
    line-height: 2.5;
  }
}
</style>
